// Gate-state hook for the Closing tab (4-step
// accordion). Reads the live pre-flight state from existing queries and
// derives per-step unlock + complete booleans.

import { useMemo } from "react";
import {
  useDealEscrowStatus,
  useDealPaymentSetup,
  useDealTpsAssignment,
  useUnderwritingCertifications,
} from "@/hooks/dealFlow/useDealFlow";
import { STEP1_CERTS, isPaymentSetupComplete, isStepOneComplete } from "./closingStepRules";

export interface ClosingTabState {
  isLoading: boolean;
  step1Complete: boolean;
  /** DISPLAY-ONLY — Step 2 (payment setup) no longer gates Step 3/4. */
  step2Complete: boolean;
  step3Complete: boolean;
  step4Complete: boolean;
  /** A servicer (TPS) decision has been recorded in Step 2 — a real TPS or an
   *  explicit keep-default. False while the decision is loading or could not
   *  be read. Soft-gates Step-4 unlock. */
  tpsDecided: boolean;
  unlocked: Record<1 | 2 | 3 | 4, boolean>;
  lockReason: Record<1 | 2 | 3 | 4, string | null>;
}

export function useClosingTabState(dealId: string): ClosingTabState {
  // Step 3 reads the server's shared escrow predicate (owner ruling 5,
  // 2026-10-08): which model the deal uses — a legacy EscrowSetup row, else its
  // Escrow.com transaction — and whether it is funded and released. Before the
  // ruling this hook derived funding itself and read completion off the legacy
  // row's releasedAt alone, so an Escrow.com deal could never complete Step 3.
  const escrow = useDealEscrowStatus(dealId);
  const paymentSetup = useDealPaymentSetup(dealId);
  const tps = useDealTpsAssignment(dealId);
  const step1Certs = useUnderwritingCertifications(dealId, "Closing", 1);
  const step3Certs = useUnderwritingCertifications(dealId, "Closing", 3);
  const step4Certs = useUnderwritingCertifications(dealId, "Closing", 4);

  return useMemo<ClosingTabState>(() => {
    const isLoading =
      escrow.isLoading ||
      paymentSetup.isLoading ||
      tps.isLoading ||
      step1Certs.isLoading ||
      step3Certs.isLoading ||
      step4Certs.isLoading;

    // Step 1 certs use the unified Pending/Certified/Declined/Escalated
    // state machine — merely saving the form (SaveDraft) creates a Pending
    // row, so completion must check for the Certified terminal status, not
    // row existence. A Declined or Escalated closing cert leaves Step 1
    // incomplete: the deal has an unresolved upstream issue and closing
    // should not proceed (a decline does not roll back the deal stage).
    const step1Complete = isStepOneComplete(step1Certs.data ?? []);

    // Step 2 is now a STATUS-ONLY surface (payment setup). It is complete for
    // DISPLAY when all three parties have finished setup, but it never gates
    // Step 3/4 — the buyer cannot finish setup pre-loan without a real
    // servicer, so gating loan creation on it would deadlock closing.
    const step2Complete = isPaymentSetupComplete(paymentSetup.data?.parties ?? []);

    const step3Complete = !!escrow.data?.released;
    const step4Complete = (step4Certs.data ?? []).some(
      (c) => c.certificationCode === "Close.S4.LoanCreated",
    );

    // Step 4 additionally soft-gates on an explicit servicer (TPS) decision
    // recorded in Step 2 — a real TPS OR an explicit keep-default. "undecided"
    // (the initial state) keeps Create Loan locked even once escrow is released,
    // and so does a decision this viewer cannot read: the read needs
    // DealFlow.WriteClosingTab, so for anyone else it fails, and while it loads
    // nothing is known either.
    const tpsDecision = tps.data?.decision;
    const tpsDecided = tpsDecision !== undefined && tpsDecision !== "undecided";

    const fundingVerified = !!escrow.data?.fundingVerified;

    const unlocked: ClosingTabState["unlocked"] = {
      1: true,
      2: step1Complete,
      // Step 2 (payment setup) is status-only, so Step 3 gates on funding
      // verification alone — never on Step 2 completion.
      3: fundingVerified,
      4: step3Complete && tpsDecided,
    };

    const lockReason: ClosingTabState["lockReason"] = {
      1: null,
      2: step1Complete ? null : "Complete Step 1 first.",
      3: fundingVerified
        ? null
        : escrow.data?.model === "Legacy"
          ? "Awaiting A1's escrow funding verification (Underwriting Section 2)."
          : "Awaiting Buyer Funded status on the deal's linked escrow transaction (Underwriting Section 2).",
      4: !step3Complete
        ? "Complete Step 3 first."
        : tpsDecision === undefined
          ? tps.isLoading
            ? "Loading the servicer (TPS) decision from Step 2."
            : "The servicer (TPS) decision in Step 2 could not be loaded."
          : !tpsDecided
            ? "Record a servicer (TPS) decision in Step 2 first."
            : null,
    };

    return {
      isLoading,
      step1Complete,
      step2Complete,
      step3Complete,
      step4Complete,
      tpsDecided,
      unlocked,
      lockReason,
    };
  }, [
    escrow.isLoading,
    escrow.data,
    paymentSetup.isLoading,
    paymentSetup.data,
    tps.isLoading,
    tps.data,
    step1Certs.isLoading,
    step1Certs.data,
    step3Certs.isLoading,
    step3Certs.data,
    step4Certs.isLoading,
    step4Certs.data,
  ]);
}

export const CLOSING_STEP1_CERTS = STEP1_CERTS;
